// Template Service — fetch/CRUD for module templates (Hook + Background per set).
// Phase 1: GOYANG only. Future: extend to ai8saat, ai16saat-half/full.
//
// Storage paths:
//   system_templates/{moduleId}/items/{templateId}  — admin-curated, read-only for users
//   users/{uid}/templates/{moduleId}/items/{templateId}  — user-owned CRUD
//
// Template shape:
//   {
//     id: string,
//     name: string,
//     description?: string,
//     isSystem: boolean,
//     set1: { hook: string, bg: string, hookCustom?: string, bgCustom?: string },
//     set2: { ... },
//     set3: { ... },
//     createdAt, updatedAt: Timestamp
//   }

import { db, auth, storage } from '../firebase';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    serverTimestamp
} from 'firebase/firestore';
import {
    ref as sRef,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';

function uid() {
    return auth.currentUser?.uid || null;
}

// ★ Templates store fragment refs by hookId/bgId (opaque UUID assigned at fragment create
// time — TIDAK derive dari nama). Decouple ID dari nama means rename fragment = update
// nama field je, tak break templates yang reference dia.
//
// Caller (TemplateEditorModal) WAJIB hantar hookId/bgId yang dah diselect dari dropdown.
// `hook`/`bg` field kekal sebagai denormalized name cache untuk display fallback.
function cleanSetPayload(setObj) {
    const out = { ...(setObj || {}) };
    // Treat 'Auto' / 'Custom' sentinel — clear ID. Real fragment selection — preserve hookId/bgId from caller.
    if (!out.hook || out.hook === 'Auto (AI decide)' || out.hook === 'Custom') out.hookId = '';
    if (!out.bg || out.bg === 'Auto (AI decide)' || out.bg === 'Custom') out.bgId = '';
    return out;
}

/**
 * Fetch list of templates available to current user (system + user's own).
 * System templates appear first, sorted by name. User templates after, sorted by updatedAt desc.
 */
export async function listTemplates(moduleId) {
    const out = [];

    // System templates
    try {
        const sysSnap = await getDocs(collection(db, 'system_templates', moduleId, 'items'));
        sysSnap.forEach(d => out.push({ ...d.data(), id: d.id, isSystem: true }));
    } catch (err) {
        console.warn('[templateService] Failed to read system_templates:', err);
    }

    // User templates
    const u = uid();
    if (u) {
        try {
            const userSnap = await getDocs(collection(db, 'users', u, 'templates', moduleId, 'items'));
            userSnap.forEach(d => out.push({ ...d.data(), id: d.id, isSystem: false }));
        } catch (err) {
            console.warn('[templateService] Failed to read user templates:', err);
        }
    }

    // Sort: system first (by name), then user (by updatedAt desc)
    out.sort((a, b) => {
        if (a.isSystem && !b.isSystem) return -1;
        if (!a.isSystem && b.isSystem) return 1;
        if (a.isSystem) return (a.name || '').localeCompare(b.name || '');
        const aTime = a.updatedAt?.toMillis?.() || 0;
        const bTime = b.updatedAt?.toMillis?.() || 0;
        return bTime - aTime;
    });

    return out;
}

/**
 * Upload custom bg image for a template. Returns { url, filename }.
 * Path: users/{uid}/{moduleId}-template-bg/{templateId}/bg_<timestamp>.<ext>
 * Validates 5MB cap + image MIME on client.
 */
export async function uploadTemplateBg(moduleId, templateId, file) {
    const u = uid();
    if (!u) throw new Error('Not authenticated');
    if (!file || !file.type?.startsWith('image/')) throw new Error('File mesti image (jpg/png/webp)');
    if (file.size > 5 * 1024 * 1024) throw new Error('Image max 5MB');
    const tplId = templateId || `tmpl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
    const filename = `bg_${Date.now()}.${ext || 'jpg'}`;
    const path = `users/${u}/${moduleId}-template-bg/${tplId}/${filename}`;
    const fileRef = sRef(storage, path);
    // Path bertimestamp → immutable. Bg template di-fetch pada SETIAP flow run GOYANG —
    // tanpa header ni ia dimuat turun penuh setiap kali (kos egress).
    await uploadBytes(fileRef, file, { contentType: file.type, cacheControl: 'public, max-age=31536000, immutable' });
    const url = await getDownloadURL(fileRef);
    return { url, filename: file.name || filename };
}

/**
 * Delete custom bg image by URL. Non-fatal — logs and continues on failure.
 * Called by saveUserTemplate (replace/remove flow), deleteUserTemplate (template removal),
 * and Modal cancel handler (pending upload rollback).
 */
export async function deleteTemplateBg(url) {
    if (!url) return;
    try {
        const fileRef = sRef(storage, url);
        await deleteObject(fileRef);
    } catch (err) {
        console.warn('[deleteTemplateBg] Failed (non-critical):', err?.message || err);
    }
}

/**
 * Save user template (create or update).
 * On update: if customBgImageUrl differs from existing, delete old bg from storage (atomic cleanup).
 * @returns {string} templateId — generated for new templates
 */
export async function saveUserTemplate(moduleId, template) {
    const u = uid();
    if (!u) throw new Error('Not authenticated');
    const id = template.id || `tmpl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const docRef = doc(db, 'users', u, 'templates', moduleId, 'items', id);

    // ★ Atomic bg cleanup — covers BOTH "remove" (newUrl='') and "replace" (newUrl=different) scenarios.
    // Only runs for updates (template.id provided); new templates have no old bg to clean.
    if (template.id) {
        try {
            const existing = await getDoc(docRef);
            const oldUrl = existing.exists() ? (existing.data().customBgImageUrl || '') : '';
            const newUrl = template.customBgImageUrl || '';
            if (oldUrl && oldUrl !== newUrl) {
                await deleteTemplateBg(oldUrl);
            }
        } catch (err) {
            console.warn('[saveUserTemplate] Old bg cleanup failed (non-fatal):', err?.message || err);
        }
    }

    const payload = {
        id,
        name: template.name || 'Untitled',
        description: template.description || '',
        customBgImageUrl: template.customBgImageUrl || '',
        customBgFilename: template.customBgFilename || '',
        handGender: template.handGender || 'auto',  // 'auto' | 'female' | 'male' — GOYANG-specific
        set1: cleanSetPayload(template.set1 || { hook: '', bg: '' }),
        set2: cleanSetPayload(template.set2 || { hook: '', bg: '' }),
        set3: cleanSetPayload(template.set3 || { hook: '', bg: '' }),
        isSystem: false,
        updatedAt: serverTimestamp()
    };
    if (!template.id) payload.createdAt = serverTimestamp();
    await setDoc(docRef, payload, { merge: true });
    return id;
}

/**
 * Delete user template. System templates cannot be deleted.
 * Pre-delete: cleanup any attached custom bg image from storage.
 */
export async function deleteUserTemplate(moduleId, templateId) {
    const u = uid();
    if (!u) throw new Error('Not authenticated');
    const docRef = doc(db, 'users', u, 'templates', moduleId, 'items', templateId);

    // Pre-delete bg cleanup (non-fatal — proceed with template deletion regardless)
    try {
        const snap = await getDoc(docRef);
        const oldUrl = snap.exists() ? (snap.data().customBgImageUrl || '') : '';
        if (oldUrl) await deleteTemplateBg(oldUrl);
    } catch (err) {
        console.warn('[deleteUserTemplate] Pre-delete bg cleanup failed:', err?.message || err);
    }

    await deleteDoc(docRef);
}

/**
 * Clone a system template into the user's own collection.
 * @returns {string} new templateId
 */
export async function cloneTemplate(moduleId, source) {
    return saveUserTemplate(moduleId, {
        name: `${source.name || 'Untitled'} (Copy)`,
        description: source.description || '',
        set1: { ...(source.set1 || {}) },
        set2: { ...(source.set2 || {}) },
        set3: { ...(source.set3 || {}) }
    });
}

// ─── Admin-only: System Template CRUD ───────────────────────────

/**
 * List system templates for a module (admin/all users — read).
 */
export async function listSystemTemplates(moduleId) {
    const snap = await getDocs(collection(db, 'system_templates', moduleId, 'items'));
    const out = [];
    snap.forEach(d => out.push({ ...d.data(), id: d.id, isSystem: true }));
    out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return out;
}

/**
 * Save system template (admin only — Firestore rules enforce).
 * @returns {string} templateId
 */
export async function saveSystemTemplate(moduleId, template) {
    const id = template.id || `sys_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const docRef = doc(db, 'system_templates', moduleId, 'items', id);
    const payload = {
        id,
        name: template.name || 'Untitled',
        description: template.description || '',
        customBgImageUrl: template.customBgImageUrl || '',
        customBgFilename: template.customBgFilename || '',
        handGender: template.handGender || 'auto',  // 'auto' | 'female' | 'male' — GOYANG-specific
        set1: cleanSetPayload(template.set1 || { hook: '', bg: '' }),
        set2: cleanSetPayload(template.set2 || { hook: '', bg: '' }),
        set3: cleanSetPayload(template.set3 || { hook: '', bg: '' }),
        isSystem: true,
        updatedAt: serverTimestamp()
    };
    if (!template.id) payload.createdAt = serverTimestamp();
    await setDoc(docRef, payload, { merge: true });
    return id;
}

/**
 * Delete system template (admin only).
 */
export async function deleteSystemTemplate(moduleId, templateId) {
    await deleteDoc(doc(db, 'system_templates', moduleId, 'items', templateId));
}

// ─── Preset Fragments (Modular Composition) ──────────────────────
// Path: preset_fragments/{moduleId}/{type}/{fragmentId}
// type: 'hooks' | 'backgrounds' | (future: 'styles' | 'cta' | etc)
//
// Fragment shape:
//   { id, name, fragment, examples[], order, enabled, isSystem }

/**
 * List preset fragments for a module + type. Sorted by `order`, then name.
 */
export async function listFragments(moduleId, type) {
    const snap = await getDocs(collection(db, 'preset_fragments', moduleId, type));
    const out = [];
    snap.forEach(d => out.push({ ...d.data(), id: d.id }));
    out.sort((a, b) => {
        if ((a.order || 0) !== (b.order || 0)) return (a.order || 0) - (b.order || 0);
        return (a.name || '').localeCompare(b.name || '');
    });
    return out;
}

/**
 * Save fragment (admin only — Firestore rules enforce).
 * Create new: generate opaque UUID. Edit: preserve fragment.id (rename = update name field je,
 * doc ID kekal — templates yang reference fragment by ID tak break).
 * @returns {string} fragmentId
 */
export async function saveFragment(moduleId, type, fragment) {
    const id = fragment.id || `frag_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const docRef = doc(db, 'preset_fragments', moduleId, type, id);
    const payload = {
        id,
        name: fragment.name || 'Untitled',
        fragment: fragment.fragment || '',
        examples: Array.isArray(fragment.examples) ? fragment.examples : [],
        order: typeof fragment.order === 'number' ? fragment.order : 99,
        enabled: fragment.enabled !== false,
        isSystem: true,
        updatedAt: serverTimestamp()
    };
    if (!fragment.id) payload.createdAt = serverTimestamp();
    await setDoc(docRef, payload, { merge: true });
    return id;
}

/**
 * Delete fragment (admin only).
 */
export async function deleteFragment(moduleId, type, fragmentId) {
    await deleteDoc(doc(db, 'preset_fragments', moduleId, type, fragmentId));
}
